"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Leaf, ShieldCheck, Ship, Globe } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pipsData = [{
  id: "sourcing",
  label: "Sourcing",
  desc: "Direct partnerships with farmers in Hyderabad for premium rice and spices.",
  x: 61.8,
  y: 66.8,
  iconName: "Leaf"
}, {
  id: "qc",
  label: "Quality Control",
  desc: "Rigorous testing ensuring authenticity and natural purity.",
  x: 50.0,
  y: 57.5,
  iconName: "ShieldCheck"
}, {
  id: "logistics",
  label: "Global Logistics",
  desc: "Efficient, climate-controlled shipping to our Apex, NC hub.",
  x: 38.1,
  y: 46.8,
  iconName: "Ship"
}];
const iconMap: Record<string, React.ElementType> = {
  Leaf: Leaf,
  ShieldCheck: ShieldCheck,
  Ship: Ship
};
export default function TheHyderabadApexConnection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="the-hyderabad-apex-connection" className="w-full bg-background py-24 overflow-hidden relative">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes flow-line {
          to { stroke-dashoffset: 0; }
        }
        @keyframes pulse-glow {
          0%, 100% { filter: drop-shadow(0 0 4px var(--color-secondary)); }
          50% { filter: drop-shadow(0 0 12px var(--color-secondary)); }
        }
        @keyframes shipping-lane {
          from { stroke-dashoffset: 100; }
          to { stroke-dashoffset: 0; }
        }
        .animate-flow {
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: flow-line 3s ease-out forwards;
        }
        .animate-glow {
          animation: pulse-glow 3s infinite ease-in-out;
        }
        .animate-shipping {
          animation: shipping-lane 20s linear infinite;
        }
      `
    }} />

      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
            The Hyderabad-Apex Connection
          </h2>
          <p className="text-lg text-muted-foreground">
            Trace the journey of our premium products from the rich agricultural hubs of India directly to our distribution center in North Carolina. A seamless bridge of global trade.
          </p>
        </div>

        <div className={`relative w-full max-w-5xl mx-auto rounded-3xl overflow-hidden border border-border/50 bg-muted/10 shadow-2xl transition-all duration-1000 delay-300 transform ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
          <AspectRatio ratio={2 / 1} className="w-full h-full relative">
            {}
            <svg viewBox="0 0 1000 500" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice">
              {}
              <g className="opacity-20 stroke-muted-foreground" fill="none" strokeWidth="1">
                {}
                <path d="M 100 100 Q 200 50 300 150 T 400 100 T 350 300 Q 200 400 150 250 Z" />
                <path d="M 600 200 Q 700 100 850 150 T 900 350 Q 800 450 700 350 T 650 250 Z" />
                <path d="M 450 350 Q 500 300 550 400 T 500 480 Z" />
                {}
                <line x1="0" y1="125" x2="1000" y2="125" strokeDasharray="4 4" className="opacity-30" />
                <line x1="0" y1="250" x2="1000" y2="250" strokeDasharray="4 4" className="opacity-30" />
                <line x1="0" y1="375" x2="1000" y2="375" strokeDasharray="4 4" className="opacity-30" />
                <line x1="250" y1="0" x2="250" y2="500" strokeDasharray="4 4" className="opacity-30" />
                <line x1="500" y1="0" x2="500" y2="500" strokeDasharray="4 4" className="opacity-30" />
                <line x1="750" y1="0" x2="750" y2="500" strokeDasharray="4 4" className="opacity-30" />
              </g>

              {}
              <g className="stroke-primary/20" fill="none" strokeWidth="2" strokeDasharray="8 8">
                <path d="M 800 400 C 600 500, 400 200, 200 250" className="animate-shipping" />
                <path d="M 700 300 C 550 200, 450 100, 300 150" className="animate-shipping" style={{
                animationDirection: 'reverse',
                animationDuration: '25s'
              }} />
                <path d="M 850 250 C 650 150, 350 300, 150 100" className="animate-shipping" style={{
                animationDuration: '30s'
              }} />
              </g>

              {}
              {isVisible && <path d="M 750 350 C 600 450, 400 150, 250 200" fill="none" stroke="var(--color-secondary)" strokeWidth="4" strokeLinecap="round" className="animate-flow animate-glow" />}
            </svg>

            {}
            <div className="absolute inset-0 w-full h-full">
              
              {}
              <div className="absolute flex flex-col items-center justify-center -translate-x-1/2 -translate-y-1/2" style={{
              left: "75%",
              top: "70%"
            }}>
                <div className="relative flex items-center justify-center">
                  <div className="absolute w-8 h-8 bg-secondary/30 rounded-full animate-ping" />
                  <div className="w-4 h-4 bg-secondary rounded-full border-2 border-background z-10 shadow-[0_0_10px_var(--color-secondary)]" />
                </div>
                <div className="mt-2 px-3 py-1 rounded-full bg-background/80 backdrop-blur-md border border-border/50 shadow-sm text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Globe className="w-3 h-3 text-secondary" />
                  Hyderabad, India
                </div>
              </div>

              {}
              <div className="absolute flex flex-col items-center justify-center -translate-x-1/2 -translate-y-1/2" style={{
              left: "25%",
              top: "40%"
            }}>
                <div className="relative flex items-center justify-center">
                  <div className="absolute w-8 h-8 bg-primary/30 rounded-full animate-ping" style={{
                  animationDelay: '1s'
                }} />
                  <div className="w-4 h-4 bg-primary rounded-full border-2 border-background z-10 shadow-[0_0_10px_var(--color-primary)]" />
                </div>
                <div className="mt-2 px-3 py-1 rounded-full bg-background/80 backdrop-blur-md border border-border/50 shadow-sm text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-primary" />
                  Apex, NC, USA
                </div>
              </div>

              {}
              <TooltipProvider delayDuration={100}>
                {pipsData.map((pip, i) => {
                const Icon = iconMap[pip.iconName];
                return <div key={pip.id} data-index={i} className={`absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-0"}`} style={{
                  left: `${pip.x}%`,
                  top: `${pip.y}%`,
                  transitionDelay: `${1000 + i * 400}ms`
                }}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="group relative flex items-center justify-center w-10 h-10 rounded-full bg-background border-2 border-secondary shadow-lg hover:scale-110 hover:bg-secondary transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                            <Icon className="w-4 h-4 text-secondary group-hover:text-secondary-foreground transition-colors" />
                            {}
                            <span className="absolute inset-0 rounded-full border border-secondary opacity-0 group-hover:animate-ping" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="bg-background/95 backdrop-blur-xl border border-border text-foreground p-4 max-w-[200px] shadow-xl">
                          <div className="space-y-1.5">
                            <h4 className="font-semibold text-sm flex items-center gap-2 text-primary">
                              <Icon className="w-4 h-4" />
                              {pip.label}
                            </h4>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {pip.desc}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>;
              })}
              </TooltipProvider>
            </div>
          </AspectRatio>
        </div>
      </div>
    </section>;
}